import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import { captureService, type CaptureListItem } from '@/services/captureApi';
import { AuthGuard } from '@/components/Auth/AuthGuard';
import {
  Loader2,
  Trash2,
  Camera,
  Search,
  ArrowUpDown,
  CheckCircle2,
  AlertCircle,
  Archive,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppShell } from '@/components/layout/AppShell';
import { ReplayStatusChip } from '@/components/captures/CaptureReplay';
import { GetExtensionDialog } from '@/components/captures/GetExtension';
import { extensionDownload } from '@/components/captures/extensionDownload';

export const Route = createFileRoute('/captures')({
  component: () => <AuthGuard><AppShell><CapturesPage /></AppShell></AuthGuard>,
});

type SortOrder = 'newest' | 'oldest';

function CapturesPage() {
  const navigate = useNavigate();
  const [captures, setCaptures] = useState<CaptureListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [download] = useState(extensionDownload);
  const [installOpen, setInstallOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOrder>('newest');

  useEffect(() => {
    loadCaptures();
  }, []);

  async function loadCaptures() {
    setLoading(true);
    try {
      const data = await captureService.list(50, 0);
      setCaptures(data.captures);
      setTotal(data.total);
    } catch (e) {
      console.error('Failed to load captures:', e);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm('Delete this capture and all its frames? This cannot be undone.')) return;
    try {
      await captureService.delete(id);
      setCaptures(c => c.filter(x => x.id !== id));
      setTotal(t => t - 1);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }

  const visible = useMemo(() => {
    let list = [...captures];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.url || '').toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      const av = new Date(a.createdAt).getTime();
      const bv = new Date(b.createdAt).getTime();
      return sort === 'newest' ? bv - av : av - bv;
    });
    return list;
  }, [captures, search, sort]);

  return (
    <div className="h-screen overflow-y-auto studio-canvas">
      <AppHeader />

      <div className="w-full px-6 md:px-10 py-10">
        {/* Page title + search */}
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight studio-text-primary">
              Captures
            </h1>
            <p className="text-sm studio-text-secondary mt-1.5">
              {total === 0 ? 'Record product demos with the Chrome extension' : `${total} demo recording${total === 1 ? '' : 's'}`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {download && (
              <button
                onClick={() => setInstallOpen(true)}
                className="h-9 inline-flex items-center gap-1.5 rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/30 backdrop-blur-md px-3 text-xs font-medium studio-text-secondary hover:studio-text-primary transition"
              >
                <Download className="h-3.5 w-3.5" />
                Get the extension
              </button>
            )}
            {total > 0 && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 studio-text-secondary pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search captures…"
                  className="h-9 w-56 rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/30 backdrop-blur-md pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
                />
              </div>
              <button
                onClick={() => setSort(s => s === 'newest' ? 'oldest' : 'newest')}
                className="h-9 inline-flex items-center gap-1.5 rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/30 backdrop-blur-md px-3 text-xs font-medium studio-text-secondary hover:studio-text-primary transition"
              >
                <ArrowUpDown className="h-3.5 w-3.5" />
                {sort === 'newest' ? 'Newest' : 'Oldest'}
              </button>
            </>
            )}
          </div>
        </div>
        {download && <GetExtensionDialog download={download} open={installOpen} onOpenChange={setInstallOpen} />}

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : captures.length === 0 ? (
          <EmptyState onInstall={download ? () => setInstallOpen(true) : undefined} />
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Search className="h-10 w-10 text-muted-foreground opacity-40 mb-3" />
            <p className="text-sm text-muted-foreground">No captures match "{search}"</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map(cap => (
              <CaptureCard
                key={cap.id}
                capture={cap}
                onOpen={() => navigate({ to: '/captures/$captureId', params: { captureId: cap.id } })}
                onDelete={(e) => handleDelete(cap.id, e)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Capture Card ───────────────────────────────────────────────────────────

interface CardProps {
  capture: CaptureListItem;
  onOpen: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

function CaptureCard({ capture, onOpen, onDelete }: CardProps) {
  return (
    <button
      onClick={onOpen}
      className={cn(
        'group text-left relative flex flex-col overflow-hidden rounded-2xl',
        'border border-black/5 dark:border-white/5',
        'bg-white/70 dark:bg-black/40 backdrop-blur-xl',
        'shadow-[var(--shadow-glass)]',
        'transition-all duration-200 will-change-transform',
        'hover:-translate-y-0.5 hover:shadow-[var(--shadow-float)] hover:border-primary/30',
        'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40',
      )}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted/50 overflow-hidden">
        {capture.thumbnailUrl ? (
          <img
            src={capture.thumbnailUrl}
            alt={capture.name}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40">
            <Camera className="h-8 w-8" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        {capture.replayStatus
          ? <ReplayStatusChip status={capture.replayStatus} summary={capture.replaySummary} />
          : <StatusBadge status={capture.status} />}
      </div>

      {/* Body */}
      <div className="flex flex-col gap-2 p-4">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold studio-text-primary">{capture.name}</h3>
          <p className="truncate text-xs studio-text-secondary mt-0.5">
            {stripProtocol(capture.url)}
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 mt-1">
          <div className="flex items-center gap-3 text-[11px] studio-text-secondary font-medium">
            <span className="inline-flex items-center gap-1">
              <span className="h-1 w-1 rounded-full bg-muted-foreground/60" />
              {capture.frameCount ?? 0} frames
            </span>
            <span>{relativeTime(capture.createdAt)}</span>
          </div>
          <button
            type="button"
            aria-label="Delete capture"
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive p-1 rounded-md hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </button>
  );
}

// ─── Status Badge ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: CaptureListItem['status'] }) {
  const config: Record<CaptureListItem['status'], { icon: typeof CheckCircle2; label: string; classes: string }> = {
    READY: {
      icon: CheckCircle2,
      label: 'Ready',
      classes: 'bg-emerald-500/90 text-white',
    },
    PROCESSING: {
      icon: Loader2,
      label: 'Processing',
      classes: 'bg-primary/90 text-primary-foreground',
    },
    FAILED: {
      icon: AlertCircle,
      label: 'Failed',
      classes: 'bg-destructive/90 text-destructive-foreground',
    },
    ARCHIVED: {
      icon: Archive,
      label: 'Archived',
      classes: 'bg-muted text-muted-foreground',
    },
  };
  const c = config[status];
  const Icon = c.icon;
  return (
    <div className={cn(
      'absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold backdrop-blur-md',
      'shadow-sm ring-1 ring-black/10',
      c.classes,
    )}>
      <Icon className={cn('h-3 w-3', status === 'PROCESSING' && 'animate-spin')} />
      {c.label}
    </div>
  );
}

// ─── Empty State ────────────────────────────────────────────────────────────

function EmptyState({ onInstall }: { onInstall?: () => void }) {
  return (
    <div className="relative flex flex-col items-center justify-center py-24 text-center">
      {/* Decorative mesh orbs for hero-consistency */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-1/4 w-64 h-64 bg-primary/20 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-10 right-1/4 w-64 h-64 bg-primary/15 rounded-full blur-3xl opacity-40" />
      </div>

      <div className="relative mb-6">
        <div className="absolute inset-0 bg-primary/25 blur-3xl rounded-full" />
        <div className="relative h-20 w-20 rounded-3xl flex items-center justify-center shadow-[0_12px_40px_hsl(var(--primary)/0.45)] bg-primary">
          <Camera className="h-9 w-9 text-primary-foreground" strokeWidth={1.8} />
        </div>
      </div>
      <h2 className="text-2xl font-bold tracking-tight mb-2 studio-text-primary">
        <span className="text-gradient-purple">No captures yet</span>
      </h2>
      <p className="text-sm studio-text-secondary max-w-md mb-6">
        Install the 4K Labs Capture Chrome extension to record product demos,
        then curate and import them into a video project.
      </p>
      <div className="relative flex items-center gap-3">
        {onInstall ? (
          <button
            onClick={onInstall}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-[0_0_0_1px_hsl(var(--primary)/0.2),0_8px_24px_hsl(var(--primary)/0.3)] hover:bg-primary/90 transition"
          >
            <Download className="h-4 w-4" />
            Install extension
          </button>
        ) : (
          <a
            href="https://chromewebstore.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-[0_0_0_1px_hsl(var(--primary)/0.2),0_8px_24px_hsl(var(--primary)/0.3)] hover:bg-primary/90 transition"
          >
            <Download className="h-4 w-4" />
            Install extension
          </a>
        )}
        <a
          href="https://beta.4klabs.ai"
          className="inline-flex items-center gap-1.5 text-sm font-medium studio-text-secondary hover:studio-text-primary transition"
        >
          Learn more
        </a>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function stripProtocol(url: string | null | undefined): string {
  if (!url) return '';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function relativeTime(date: string | Date): string {
  const then = typeof date === 'string' ? new Date(date) : date;
  const diff = Date.now() - then.getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
